import { getCurrentLayer, getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { useAppSelector } from "@builder/store/store";
import { BaseLayer, BuildNode, ConditionalVariation, SmartGridNode, SmartTileTypes } from "@common/build/types";
import "./style.scss";
import useFormatCondition from "@builder/hooks/useFormatCondition";
import OliverText from "@builder/components/general/OliverText";
import RenderFromNode from "./RenderFromNode";
import SelectedMenu from "./SelectedMenu";

const deepMerge = (left: any, right: any): any => {
    if (Array.isArray(right)) {
        return right.slice();
    }

    if (typeof right !== "object" || right === null) {
        return right;
    }

    if (typeof left !== "object" || left === null || Array.isArray(left)) {
        return { ...right };
    }

    const result: Record<string, any> = { ...left };

    for (const key of Object.keys(right)) {
        result[key] = deepMerge(left[key], right[key]);
    }

    return result;
};

export const checkSmartTileAllowed = (node: BuildNode, conditionalVariation: ConditionalVariation, baseLayer: BaseLayer) => {
    const smartGridId = node.parent;
    const conditionSplit = [...conditionalVariation.condition.matchAll(/\[(.*?)\]/g)].map(m => m[1]);
    if (!smartGridId) return false;
    if (!conditionSplit || conditionSplit.length !== 2) return false;
    const smartGrid = baseLayer.components[smartGridId] as SmartGridNode;
    if (!smartGrid) return false;
    return smartGrid.smartGrid?.allowedTypes?.includes(conditionSplit[1] as SmartTileTypes);
};

export const buildVariation = (node: BuildNode, index: number, appendId?: string) => {
    const variation = node.conditionalVariations?.[index];

    if (!variation) return null;

    let additionalStyle = {};

    const { conditionalVariations, ...cvNode } = node;
    const { children, nodeProps } = variation;

    const classNameArray = cvNode.attr?.className?.split(" ") || [];
    const newClassName = classNameArray.filter(className => className !== "builder-selected" && className !== "builder-hover").join(" ");

    const conditionSplit = [...variation.condition.matchAll(/\[(.*?)\]/g)].map(m => m[1]);
    const smartTileType = conditionSplit[1];

    const overrideNode = {
        children,
        ...nodeProps,
        id: `${cvNode.id}|${index}${appendId ? `|${appendId}` : ""}`,
        attr: {
            ...nodeProps?.attr,
            className: newClassName,
            style: {
                ...nodeProps?.attr?.style,
                ...additionalStyle
            }
        },
        smartTileType
    };

    const variationNode = deepMerge(cvNode, overrideNode);

    return variationNode;
};

const RenderVariationNodes = () => {
    const baseLayer = useAppSelector(getCurrentLayer);
    const cvMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const formatCondition = useFormatCondition();
    const selectedNodeId = useAppSelector(getSelectedNode);

    const cvNode = baseLayer.components[cvMode?.id || ""];
    const parentNode = baseLayer.components[cvNode?.parent as string];

    let parentDimensions = cvMode?.parentDimensions;
    if (parentNode && parentNode.name === "smartGrid" && parentDimensions) {
        const smartGridNode = parentNode as SmartGridNode;
        const columns = smartGridNode.smartGrid?.columns;
        const rows = smartGridNode.smartGrid?.rows;

        parentDimensions = {
            width: columns ? parentDimensions.width / columns : parentDimensions.width,
            height: rows ? parentDimensions.height / rows : parentDimensions.height
        };
    }

    if (!cvNode) return null;

    return (
        <div className="conditional-variation-mode-wrapper">
            {cvNode.conditionalVariations?.map((_, index) => {
                const variation = buildVariation(cvNode, index);
                if (!variation) return null;
                return (
                    <div className="conditional-variation-wrapper" key={index}>
                        <div className="conditional-variation-header">
                            <img src="/fork.svg" />
                            <OliverText text={formatCondition(cvNode.conditionalVariations?.[index]?.condition || "")} />
                        </div>
                        {parentDimensions && (
                            <div
                                className="conditional-variation-parent-dimensions"
                                style={{ width: parentDimensions.width + "px", height: parentDimensions.height + "px" }}
                            >
                                <RenderFromNode node={variation} noSelectedMenu />
                            </div>
                        )}
                        {!cvMode?.parentDimensions && <RenderFromNode node={variation} noSelectedMenu />}
                    </div>
                );
            })}
            {selectedNodeId && <SelectedMenu />}
        </div>
    );
};

export default RenderVariationNodes;
